import axios from 'axios';

const API_BASE_URL = (typeof window !== 'undefined' && window.__API_BASE__) || process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add token to requests
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

function clearAuthStorage() {
  localStorage.removeItem('token');
  localStorage.removeItem('tok');
  localStorage.removeItem('user');
  try {
    const keys = Object.keys(sessionStorage);
    keys.forEach((key) => {
      if (key.startsWith('packages_cache_')) {
        sessionStorage.removeItem(key);
      }
    });
  } catch (e) {
    console.warn('Failed to clear packages cache from sessionStorage:', e);
  }
}

// Handle response errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    const code = error.response?.data?.code;
    if (error.response?.status === 403 && code === 'FREE_TRIAL_EXPIRED') {
      const isCheckout = error.config?.url?.includes('/stripe/create-checkout');
      if (!isCheckout) {
        clearAuthStorage();
        window.location.replace(`${window.location.origin}/login?trial_expired=1`);
      }
      return Promise.reject(error);
    }
    if (error.response?.status === 401) {
      const isLoginRequest = error.config?.url?.includes('/login') || error.config?.url?.endsWith('login');
      // Do not redirect when login itself failed (wrong password) — let Login.js show the persistent error
      if (isLoginRequest) {
        return Promise.reject(error);
      }
      clearAuthStorage();
      // Redirect to login
      window.location.replace(window.location.origin + '/login');
    }
    return Promise.reject(error);
  }
);

export const authAPI = {
  signup: (email, password, plan = 'free_trial') => api.post('/signup', { email, password, plan }),
  login: (login, password) => api.post('/login', { login, password, email: login }),
  verifyEmail: (token) => api.post('/verify-email', { token }),
  resendVerification: (email) => api.post('/resend-verification', { email }),
  forgotPassword: (email) => api.post('/forgot-password', { email }),
  resetPassword: (token, password) => api.post('/reset-password', { token, password }),
};

export const accountAPI = {
  getMe: () => api.get('/me'),
  updatePlan: (plan) => api.patch('/account/plan', { plan }),
  changeEmail: (newEmail, password) =>
    api.patch('/account/email', { new_email: newEmail, password }),
  changePassword: (currentPassword, newPassword) =>
    api.patch('/account/password', { current_password: currentPassword, new_password: newPassword }),
  getProductLogin: () => api.get('/account/product-login'),
  setProductLogin: (username, password, centralPassword) =>
    api.put('/account/product-login', { username, password, central_password: centralPassword }),
  disableProductLogin: (centralPassword) =>
    api.delete('/account/product-login', { data: { central_password: centralPassword } }),
};

export const contactAPI = {
  submit: (data) => api.post('/contact', {
    first_name: data.firstName,
    last_name: data.lastName,
    email: data.email,
    phone: data.phone || undefined,
    message: data.message,
  }),
};

const apiBase = (typeof window !== 'undefined' && window.__API_BASE__) || process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
const backendBase = apiBase.replace(/\/api\/?$/, '') || 'http://localhost:5000';

export const oauthAPI = {
  getAuthorizeUrl: () => api.get('/auth/oracle/authorize'),
  refreshToken: (connectionId) => api.post('/auth/oracle/refresh', { connection_id: connectionId }),
};

export const samlOAuthAPI = {
  getStatus: () => api.get('/auth/saml/status'),
};

export const getSamlLoginUrl = () => `${backendBase}/auth/saml/login`;

export const samlAPI = {
  initiate: (connectionName, hcmUrl) => api.get('/auth/saml/initiate', { 
    params: { connection_name: connectionName, hcm_url: hcmUrl } 
  }),
};

export const ssoAPI = {
  getConnections: () => api.get('/sso/connections'),
  createConnection: (name, url) => api.post('/sso/connections', { name, url }),
};

export const getSamlLoginUrlWithTenant = (tenantId) => `${backendBase}/auth/saml/login?tenantId=${encodeURIComponent(tenantId)}`;

/** Omit or empty report_path to use server default report */
export const connectionsAPI = {
  getAll: () => api.get('/connections'),
  create: (data) => api.post('/connections', data),
  update: (id, data) => api.put(`/connections/${id}`, data),
  delete: (id) => api.delete(`/connections/${id}`),
  test: (id) => api.post(`/connections/${id}/test`),
  runReport: (id, body, options = {}) =>
    api.post(`/connections/${id}/runReport`, body, { signal: options.signal }),
  installCatalog: (id) => api.post(`/connections/${id}/installCatalog`),
};

export const queryAPI = {
  execute: (connectionId, query, method = 'rest', mode = 'sql', options = {}, rowLimit = null) =>
    api.post('/query/execute', {
      connection_id: connectionId,
      query,
      method,
      mode,
      row_limit: rowLimit,
      ...(options.sqlEndpointPath != null && options.sqlEndpointPath !== '' && { sql_endpoint_path: options.sqlEndpointPath })
    }, { signal: options.signal }),
  format: (query) => api.post('/query/format', { query }),
  validate: (query, options = {}) => api.post('/query/validate', { query }, { signal: options.signal }),
  /**
   * Paginated execution history (newest first). Pass a number (limit) or { limit, skip }.
   * Backend caps limit at 1000; defaults used when omitting opts.
   */
  getHistory: (limitOrOptions = 500) => {
    const opts = typeof limitOrOptions === 'number' ? { limit: limitOrOptions } : limitOrOptions || {};
    const limit = Math.min(Math.max(1, opts.limit ?? 500), 1000);
    const skip = Math.max(0, opts.skip ?? 0);
    return api.get('/history', { params: { limit, skip } });
  },
  getTables: (connectionId, filter = '') =>
    api.post('/tables', { connection_id: connectionId, filter }),
  getTableColumns: (connectionId, tableName) =>
    api.post('/table-columns', { connection_id: connectionId, table_name: tableName }),
  checkTables: (connectionId, tableNames) =>
    api.post('/tables-check', { connection_id: connectionId, table_names: tableNames }),
  getPackages: (connectionId) =>
    api.post('/packages', { connection_id: connectionId }),
  getHcmRestResources: (connectionId) =>
    api.post('/query/hcm-rest-resources', { connection_id: connectionId }),
  restRequest: (connectionId, url, method, headers, body, options = {}) =>
    api.post('/query/rest-request', { connection_id: connectionId || null, url, method, headers, body }, { signal: options.signal }),
};

export const aiAPI = {
  generateSql: (prompt, tableNames = [], dialect = 'oracle') =>
    api.post('/ai/generate-sql', { prompt, tableNames, dialect }),
};

export const exportAPI = {
  exportData: async (format, columns, data, filename) => {
    try {
      const response = await api.post(
        '/export',
        { format, columns, data, filename },
        { 
          responseType: 'blob',
          validateStatus: function (status) {
            // Accept both success (200) and error responses to handle them properly
            return status >= 200 && status < 500;
          }
        }
      );
      
      // Check if response is an error (JSON blob)
      if (response.headers['content-type'] && response.headers['content-type'].includes('application/json')) {
        // Try to parse error message from blob
        const text = await response.data.text();
        let errorMsg = 'Export failed';
        try {
          const errorData = JSON.parse(text);
          errorMsg = errorData.error || errorMsg;
        } catch (e) {
          errorMsg = text || errorMsg;
        }
        throw new Error(errorMsg);
      }
      
      // Create download link for successful blob response
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      
      const extension = format === 'xls' ? 'xlsx' : (format === 'hdl' || format === 'dat') ? 'dat' : format === 'pdf' ? 'pdf' : 'csv';
      link.setAttribute('download', `${filename}.${extension}`);
      
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      // Clean up the object URL after a short delay
      setTimeout(() => window.URL.revokeObjectURL(url), 100);
      
      return response;
    } catch (error) {
      // Re-throw with better error message
      if (error.response && error.response.data) {
        // Try to extract error message from blob response
        if (error.response.data instanceof Blob) {
          try {
            const text = await error.response.data.text();
            const errorData = JSON.parse(text);
            throw new Error(errorData.error || 'Export failed');
          } catch (e) {
            throw new Error(error.message || 'Export failed');
          }
        }
      }
      throw error;
    }
  },
};

export const worksheetAPI = {
  getAll: () => api.get('/worksheets'),
  create: (data) => api.post('/worksheets', data),
  update: (id, data) => api.put(`/worksheets/${id}`, data),
  delete: (id) => api.delete(`/worksheets/${id}`),
};

export default api;

